import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Link } from "react-router-dom";

export default function DailyCall() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [voting, setVoting] = useState(false);

  const load = () => {
    api.get("/daily-call/current").then((r) => setData(r.data)).catch(() => {});
  };

  useEffect(() => {
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, []);

  const vote = async (direction) => {
    if (!user) return toast.error("Sign in to vote");
    setVoting(true);
    try {
      await api.post("/daily-call/vote", { direction });
      toast.success("Locked in!");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not vote");
    } finally { setVoting(false); }
  };

  if (!data) return null;

  const deadline = new Date(data.target_date + "T00:00:00Z");
  const hoursLeft = Math.max(0, Math.round((deadline - new Date()) / (1000 * 60 * 60)));
  const deadlineLabel = deadline.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" });

  return (
    <div className="bg-gradient-to-br from-yellow-400/[0.06] to-transparent border border-[var(--border-soft)] rounded-2xl p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-full bg-orange-400 flex items-center justify-center font-bold text-black text-sm">₿</div>
          <div>
            <div className="text-sm font-bold text-[var(--text-primary)]">BTC</div>
            <div className="text-xs font-mono text-[var(--text-tertiary)]">${data.current_price?.toLocaleString()}</div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-green-400">
          <span className="w-1.5 h-1.5 rounded-full bg-green-400" /> {data.total_votes} predictions
        </div>
      </div>

      <p className="text-sm text-[var(--text-primary)] leading-relaxed mb-4">
        Will Bitcoin close <b>above</b> today's price by <b>{deadlineLabel}</b>?
      </p>

      <div className="flex justify-between text-[11px] font-mono text-[var(--text-tertiary)] mb-1.5">
        <span>{data.above_pct}% predict Above</span>
        <span>{data.below_pct}% predict Below</span>
      </div>
      <div className="h-2 rounded-full overflow-hidden flex mb-5">
        <div className="bg-green-400" style={{ width: data.above_pct + "%" }} />
        <div className="bg-red-400" style={{ width: data.below_pct + "%" }} />
      </div>

      <div className="flex gap-2.5 mb-5">
        <button
          onClick={() => vote("above")}
          disabled={voting || !!data.my_vote}
          className={"flex-1 py-4 rounded-xl border-[1.5px] text-center transition disabled:cursor-not-allowed " +
            (data.my_vote === "above" ? "bg-green-400/15 border-green-400" : "border-[var(--border-soft)] hover:border-green-400/50")}
        >
          <div className="text-[11px] uppercase tracking-wide font-bold text-green-400">↑ Above</div>
          <div className="text-xl font-bold text-green-400 mt-1">{data.above_pct}%</div>
        </button>
        <button
          onClick={() => vote("below")}
          disabled={voting || !!data.my_vote}
          className={"flex-1 py-4 rounded-xl border-[1.5px] text-center transition disabled:cursor-not-allowed " +
            (data.my_vote === "below" ? "bg-red-400/15 border-red-400" : "border-[var(--border-soft)] hover:border-red-400/50")}
        >
          <div className="text-[11px] uppercase tracking-wide font-bold text-[var(--text-tertiary)]">↓ Below</div>
          <div className="text-xl font-bold text-[var(--text-tertiary)] mt-1">{data.below_pct}%</div>
        </button>
      </div>

      <div className="flex justify-between items-center bg-[var(--bg-elevated)] border border-[var(--border-soft)] rounded-xl px-4 py-3 mb-4 text-sm">
        <span className="text-[11px] uppercase tracking-wide text-[var(--text-tertiary)]">Locks In</span>
        <span className="text-[var(--text-primary)] font-mono">{hoursLeft}h</span>
      </div>

      {data.my_vote ? (
        <p className="text-xs text-center text-[var(--text-tertiary)]">
          You predicted <span className={data.my_vote === "above" ? "text-green-400 font-bold" : "text-red-400 font-bold"}>{data.my_vote}</span>. Resolves automatically at close.
        </p>
      ) : user ? (
        <p className="text-xs text-center text-[var(--text-tertiary)]">
          Correct call = <span className="text-yellow-400 font-bold">+1 accuracy</span>, tracked publicly on your profile.
        </p>
      ) : (
        <Link to="/login" className="block text-xs text-center text-yellow-400 hover:underline">Sign in to make your call</Link>
      )}
    </div>
  );
}